import store from "@/store";
import config from "@/config";
import { TENANCY_SETTINGS } from "@/config";
import TenantService from "@/services/tenant.service";

const countryToLocaleMap =
  (typeof window !== "undefined" && window.__COUNTRY_TO_LOCALE_MAP__) || {};
const TENANT_URL_REGEX =
  /https:\/\/(?:dev-)?plus-([a-z]{2})\.bsplus\.bridgestone\.eu/i;
const normalizeLanguageId = (value) => String(value || "").trim().toLowerCase();
const getLocaleLanguageId = (locale) =>
  normalizeLanguageId(String(locale || "").split("-")[0]);

const extractCountryCode = (url) => {
  if (!TENANCY_SETTINGS.TENANCY) {
    console.warn("TENANCY is not enabled. No country code will be extracted.");
    return null;
  }

  if (TENANCY_SETTINGS.TENANCY_LOCAL) {
    return TENANCY_SETTINGS.TENANCY_COUNTRY;
  }

  return url.match(TENANT_URL_REGEX)?.[1]?.toUpperCase() || null;
};

export function getBaseURL(tenant) {
  if (!tenant) {
    console.warn("No tenant provided. Returning default base URL.");
    return config.URL;
  }

  return TENANCY_SETTINGS.TENANCY
    ? config.URL.replace("{tenant}", tenant.toLowerCase())
    : config.URL;
}

const loadTenantConfig = async (countryCode) => {
  if (!countryCode) {
    console.error("No country code provided. Cannot load tenant configuration.");
    return;
  }

  try {
    await store.dispatch("loadTenantConfig", countryCode);
    console.log("Loaded Tenant Config from Store:", {
      availableModules: store.getters.availableModules,
      tenantId: store.getters.tenantId,
    });
  } catch (error) {
    console.error(
      `Error loading tenant configuration for country: ${countryCode}`,
      error
    );
  }
};

const mapAssignedToLocales = (assignedLanguageIds, availableLocales, countryCode) => {
  if (!Array.isArray(assignedLanguageIds) || !assignedLanguageIds.length) {
    return null;
  }

  const mappedLocales = assignedLanguageIds
    .map((languageId) => {
      const normalizedLanguageId = normalizeLanguageId(languageId);
      if (!normalizedLanguageId) {
        return null;
      }

      const candidates = availableLocales.filter(
        (locale) => getLocaleLanguageId(locale) === normalizedLanguageId
      );

      return (
        candidates.find((locale) => locale.endsWith(`-${countryCode}`)) ||
        candidates[0] ||
        null
      );
    })
    .filter(Boolean);

  return mappedLocales.length ? [...new Set(mappedLocales)] : null;
};

const getAssignedLocalesFromPlusApi = async (countryCode, availableLocales) => {
  if (!Array.isArray(availableLocales) || !availableLocales.length) {
    return null;
  }

  try {
    const response = await TenantService.publicTenantLanguages(
      getBaseURL(countryCode),
      countryCode
    );
    if (!response?.data) {
      return null;
    }

    return mapAssignedToLocales(
      Array.isArray(response.data?.assigned) ? response.data.assigned : [],
      availableLocales,
      countryCode
    );
  } catch (error) {
    console.warn("Could not load assigned locales from Plus API.", error);
    return null;
  }
};

const detectCountryAndLoadConfig = async () => {
  const countryCode = extractCountryCode(window.location.href);

  if (!countryCode || !countryToLocaleMap[countryCode]) {
    throw new Error(
      `Country code ${
        countryCode || "undefined"
      } is not defined in countryToLocaleMap.`
    );
  }

  const { default: defaultLocale, available: staticAvailableLocales } =
    countryToLocaleMap[countryCode];
  const assignedLocales =
    (await getAssignedLocalesFromPlusApi(countryCode, staticAvailableLocales)) ||
    null;
  const availableLocales = assignedLocales || staticAvailableLocales;
  const resolvedDefaultLocale = availableLocales.includes(defaultLocale)
    ? defaultLocale
    : availableLocales[0];

  console.log("Detected Country Code / Tenant:", countryCode);
  console.log("Default Locale:", resolvedDefaultLocale);
  console.log("Available Locales:", availableLocales);

  const storedLocale = localStorage.getItem("currentLocale");
  if (!storedLocale) {
    console.log(
      `No locale found in localStorage. Setting default: ${resolvedDefaultLocale}`
    );
    localStorage.setItem("currentLocale", resolvedDefaultLocale);
  } else {
    console.log(`Locale already set in localStorage: ${storedLocale}`);
  }

  await loadTenantConfig(countryCode);

  const baseURL = getBaseURL(countryCode);
  console.log("Constructed Base URL:", baseURL);

  return {
    countryCode,
    baseURL,
    defaultLocale: resolvedDefaultLocale,
    availableLocales,
  };
};

export {
  detectCountryAndLoadConfig,
  extractCountryCode,
  loadTenantConfig,
  countryToLocaleMap,
};
